// ApprovalCard — one row in the mobile Inbox "Approvals" tier (SLICE #7c-1).
//
// Renders a single pending approval (a `research_items` row in_review, or a `research_output_drafts`
// row awaiting review) with Approve / Reject buttons that call the live `mobile_approve_item` RPC.
//
// The RPC is an OPTIMISTIC-CONCURRENCY write: `_expected_updated_at` is the row's current `updated_at`
// token. The card OWNS its own token via local state so a `stale` loss can re-seed the token from the
// RPC's returned `current.updated_at` and a retry can then succeed without a full refetch. The parent
// passes the initial token + an `onResolved(decision)` callback to drop/disable the row when the write
// lands (approve/reject/already_applied) or when the row no longer exists (not_found).
//
// Offline: approvals are LIVE writes — they are NOT queued. When `online` is false the buttons are
// disabled with a clear "needs a connection" hint (mirrors the brief's hard constraint).
//
// CLUSTER-3 (research-visibility): research `item` cards LAZILY fetch a little decision context —
// primary_axis, conviction, and an ~80-char summary — via the mobile_item_detail RPC, so the user no
// longer approves blind (title + status only). The fetch is best-effort and OFFLINE-GRACEFUL: any failure
// (offline, 42501, missing) simply leaves the card exactly as it was, and it NEVER touches the
// approve/reject flow. Drafts have no equivalent detail RPC, so context is fetched for items only.

import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Check, X, FileText, ClipboardCheck, WifiOff, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { humanise } from '@/components/research-hub/labels';

export type ApprovalKind = 'item' | 'draft';
export type ApprovalDecision = 'approve' | 'reject';

export interface ApprovalCardProps {
  /** Row id (research_items.id or research_output_drafts.id). */
  id: string;
  /** Which table/RPC contract this row belongs to. */
  kind: ApprovalKind;
  /** Display title (items have one; drafts derive it from output_kind + external_id). */
  title: string;
  /** Plain-text status label to show as meta. */
  status: string;
  /** The row's CURRENT updated_at — the optimistic-concurrency token. */
  updatedAt: string;
  /** Synchronous reachability — when false the action buttons are disabled. */
  online: boolean;
  /**
   * Called once the row reaches a terminal outcome (applied, already-applied, or gone) so the parent
   * can drop it from the list. Not called on `stale` (the card re-seeds its token and stays put for a
   * retry) nor on transient/permission errors (the card surfaces a toast and stays interactive).
   */
  onResolved: (decision: ApprovalDecision | 'gone') => void;
}

// mobile_approve_item return envelope (types are stale for this RPC → described locally).
interface ApproveResult {
  ok?: boolean;
  reason?: 'approve' | 'reject' | 'already_applied' | 'stale' | 'not_found' | string;
  current?: { updated_at?: string | null } & Record<string, unknown>;
}

// mobile_item_detail return envelope (types stale → described locally). We only read a few fields here.
interface ItemDetailResult {
  ok?: boolean;
  item?: {
    primary_axis?: string | null;
    conviction?: number | null;
    summary?: string | null;
  } | null;
}

// The small decision-context we surface on an item card.
interface ApprovalContext {
  axis: string | null;
  conviction: number | null;
  summary: string | null;
}

// Trim a summary to ~80 chars on a word boundary, with an ellipsis when cut.
function shortSummary(s: string | null | undefined): string | null {
  if (!s) return null;
  const clean = s.replace(/\s+/g, ' ').trim();
  if (!clean) return null;
  if (clean.length <= 80) return clean;
  const cut = clean.slice(0, 80);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

function relativeOrNull(iso: string): string | null {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

export function ApprovalCard({
  id,
  kind,
  title,
  status,
  updatedAt,
  online,
  onResolved,
}: ApprovalCardProps) {
  const { toast } = useToast();
  // The concurrency token is owned here so a `stale` loss can re-seed it from the RPC's `current`.
  const [token, setToken] = useState(updatedAt);
  // Per-row busy state — guards against double-submit while a decision is in flight.
  const [busy, setBusy] = useState<ApprovalDecision | null>(null);
  // Lazily-fetched decision context (items only). null until/unless a successful fetch lands — any failure
  // leaves it null so the card simply renders without context (offline-graceful, no error surfaced).
  const [context, setContext] = useState<ApprovalContext | null>(null);

  // Best-effort context fetch: items only, once, when online. Never throws into the UI; a failed/empty
  // result is indistinguishable from "no context" and degrades silently. Does NOT block or affect approve.
  useEffect(() => {
    if (kind !== 'item' || !online || context) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await (supabase.rpc as any)('mobile_item_detail', { _item_id: id });
        if (cancelled || error) return;
        const res = (data ?? {}) as ItemDetailResult;
        if (res.ok !== true || !res.item) return;
        const next: ApprovalContext = {
          axis: res.item.primary_axis ? humanise(res.item.primary_axis) : null,
          conviction:
            typeof res.item.conviction === 'number' && Number.isFinite(res.item.conviction)
              ? res.item.conviction
              : null,
          summary: shortSummary(res.item.summary),
        };
        // Only set if there's actually something to show — avoids an empty context container.
        if (next.axis || next.conviction != null || next.summary) setContext(next);
      } catch {
        // Offline / transient — leave context null; the card stays exactly as it was.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, online, context, id]);

  const KindIcon = kind === 'draft' ? FileText : ClipboardCheck;
  const kindLabel = kind === 'draft' ? 'Draft output' : 'Research item';
  const when = relativeOrNull(token);

  async function decide(decision: ApprovalDecision) {
    if (busy || !online) return;
    setBusy(decision);
    try {
      const { data, error } = await (supabase.rpc as any)('mobile_approve_item', {
        _item_id: id,
        _kind: kind,
        _expected_updated_at: token,
        _decision: decision,
      });

      if (error) {
        // 42501 == permission denied (the user can SEE the row via the review branch but lacks the
        // approve/write capability). Surface a clear, non-fatal message; leave the row interactive.
        const denied = (error as { code?: string }).code === '42501';
        toast({
          variant: 'destructive',
          title: denied ? 'Not allowed' : 'Couldn’t apply',
          description: denied
            ? 'You don’t have permission to action this item.'
            : 'Something went wrong — please try again.',
        });
        return;
      }

      const res = (data ?? {}) as ApproveResult;
      const reason = res.reason;

      // `reason` is authoritative — the RPC always returns one ('approve'|'reject'|'already_applied'|
      // 'stale'|'not_found'); do NOT short-circuit on `res.ok` (a `stale` carries ok:false but, were a
      // future build to return ok:true alongside reason:'stale', branching on ok would wrongly drop the
      // row instead of re-seeding the token below).
      // Applied (or already applied by someone else) → terminal success; parent drops the row.
      if (reason === 'approve' || reason === 'reject' || reason === 'already_applied') {
        toast({
          title: reason === 'already_applied' ? 'Already actioned' : decision === 'approve' ? 'Approved' : 'Rejected',
          description:
            reason === 'already_applied'
              ? 'This was already decided elsewhere.'
              : `“${title}” ${decision === 'approve' ? 'approved' : 'rejected'}.`,
        });
        onResolved(decision);
        return;
      }

      // Optimistic-concurrency loss: someone changed the row first. Re-seed the token from the fresh
      // row so an immediate retry can succeed; keep the card in place.
      if (reason === 'stale') {
        const fresh = res.current?.updated_at;
        if (typeof fresh === 'string' && fresh) setToken(fresh);
        toast({
          variant: 'destructive',
          title: 'Changed elsewhere',
          description: 'This item was updated — refreshed, please try again.',
        });
        return;
      }

      // Row no longer exists → drop it.
      if (reason === 'not_found') {
        toast({ title: 'No longer available', description: 'This item has gone.' });
        onResolved('gone');
        return;
      }

      // Unknown reason — be conservative: surface it, keep the row.
      toast({
        variant: 'destructive',
        title: 'Couldn’t apply',
        description: 'Unexpected response — please try again.',
      });
    } catch {
      // Network/transient failure (e.g. dropped mid-write). Keep the row; let the user retry.
      toast({
        variant: 'destructive',
        title: 'Couldn’t reach the server',
        description: 'Check your connection and try again.',
      });
    } finally {
      setBusy(null);
    }
  }

  const actionsDisabled = !online || busy != null;

  return (
    <li className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start gap-2">
        <KindIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold leading-snug">{title}</p>
          <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
            {kindLabel} · {status}
            {when ? ` · updated ${when}` : ''}
          </p>

          {/* Decision context (items only; best-effort, offline-graceful). */}
          {context && (
            <>
              {(context.axis || context.conviction != null) && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {context.axis && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {context.axis}
                    </span>
                  )}
                  {context.conviction != null && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      Conviction {context.conviction}/5
                    </span>
                  )}
                </div>
              )}
              {context.summary && (
                <p className="mt-2 text-sm text-muted-foreground">{context.summary}</p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => decide('approve')}
          disabled={actionsDisabled}
          aria-label={`Approve ${title}`}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            'border-primary/30 bg-primary/10 text-primary active:bg-primary/20',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {busy === 'approve' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="h-4 w-4" aria-hidden="true" />
          )}
          Approve
        </button>
        <button
          type="button"
          onClick={() => decide('reject')}
          disabled={actionsDisabled}
          aria-label={`Reject ${title}`}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            'border-border bg-muted/40 text-foreground active:bg-muted',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {busy === 'reject' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <X className="h-4 w-4" aria-hidden="true" />
          )}
          Reject
        </button>
      </div>

      {!online && (
        <p className="mt-2 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-amber-600">
          <WifiOff className="h-3 w-3" aria-hidden="true" />
          Needs a connection to action
        </p>
      )}
    </li>
  );
}

export default ApprovalCard;
